import { useState } from 'react';
import { Mail, CheckCircle, AlertCircle, X } from 'lucide-react';

interface EmailConfirmationModalProps {
  email: string;
  onClose: () => void;
  onResend: () => Promise<void>;
}

export function EmailConfirmationModal({ email, onClose, onResend }: EmailConfirmationModalProps) {
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  const handleResend = async () => {
    setIsResending(true);
    setResendError('');
    setResendSuccess(false);

    try {
      await onResend();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error: any) {
      setResendError(error.message || 'Failed to resend confirmation email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white border border-stone rounded-4xl shadow-soft-xl max-w-md w-full overflow-hidden">
        <div className="p-8 space-y-6">
          <div className="flex justify-between items-start">
            <div className="flex-1">
              <div className="flex justify-center mb-6">
                <div className="bg-olive/10 p-5 rounded-full border border-olive/20">
                  <Mail className="w-12 h-12 text-olive" />
                </div>
              </div>
              <h2 className="text-3xl font-serif font-light tracking-tight text-center mb-2 text-charcoal">
                Verify Your Email
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-sand hover:text-charcoal transition-colors duration-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="text-center space-y-4">
            <p className="text-sand font-light">
              We sent a confirmation email to:
            </p>
            <p className="font-medium text-charcoal bg-alabaster px-5 py-2.5 rounded-full border border-stone inline-block">
              {email}
            </p>
          </div>

          <div className="bg-alabaster border border-stone rounded-3xl p-5 space-y-2">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium mb-1 text-charcoal">Please check your inbox</p>
                <p className="text-sand font-light">
                  Click the confirmation link in the email to activate your account and sign in.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <div className="text-sm text-sand text-center font-light">
              Didn't receive the email?
            </div>

            {resendSuccess && (
              <div className="bg-olive/10 border border-olive/30 rounded-2xl p-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-olive flex-shrink-0" />
                <p className="text-sm text-olive font-light">
                  Confirmation email sent! Check your inbox.
                </p>
              </div>
            )}

            {resendError && (
              <div className="bg-terracotta/10 border border-terracotta/30 rounded-2xl p-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-terracotta flex-shrink-0" />
                <p className="text-sm text-terracotta font-light">{resendError}</p>
              </div>
            )}

            <button
              onClick={handleResend}
              disabled={isResending || resendSuccess}
              className="w-full bg-olive hover:bg-charcoal text-alabaster py-3.5 rounded-full font-medium transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft hover:shadow-lift hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              {isResending ? (
                <>
                  <div className="w-5 h-5 border-2 border-alabaster border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : resendSuccess ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>Email Sent!</span>
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  <span>Resend Confirmation Email</span>
                </>
              )}
            </button>
          </div>

          <div className="pt-4 border-t border-stone">
            <div className="text-xs text-sand space-y-1.5 font-light">
              <p className="flex items-center gap-2">
                <span className="text-olive">•</span>
                Check your spam folder
              </p>
              <p className="flex items-center gap-2">
                <span className="text-olive">•</span>
                The email will arrive within a few minutes
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
